import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Home,
  TrendingUp,
  Inbox,
  Package,
  Users,
  ShoppingCart,
  PlusCircle,
  Tags,
  Newspaper,
  LogOut,
  Search,
  Bell,
  Menu,
  MessageCircle,
  Settings,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";

// Import all your page components
import Analytics from "../Analytics/Analytics";
import Imbox from "../Inbox/Imbox";
import Products from "../Products/Products";
import AProductDet from "../AProductDet/AProductDet";
import AProductUpdat from "../AProductUpdat/AProductUpdat";
import AddProductA from "../AddProductA/AddProductA";
import ACustomers from "../ACustomers/ACustomers";
import AOrders from "../AOrders/AOrders";
import ACustomerDet from "../ACustomerDet/ACustomerDet";
import AodersDet from "../AodersDet/AodersDet";
import AddFournisseur from "../AddFournisseurs/AddFournisseur";
import AddCategorie from "../AddCategorie/AddCategorie";
import ProductPub from "../ProductPub/ProductPub";
import Overview from "../Overview/Overview";
import Livraison from "../Livraisons/Livraison";
import AdminSettings from "../AdminSettings/AdminSettings";
import PromoCodes from "../PromoCodes/PromoCodes";
import AddEditPromoCode from "../PromoCodes/AddEditPromoCode";
import PromoCodeDetail from "../PromoCodes/PromoCodeDetail";

// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04", // Orange plus vif
  white: "#FFFFFF",
  green: "#008751", // Vert du drapeau nigérien
  darkGreen: "#006B3F", // Vert foncé pour les hovers
  lightOrange: "#FFE0B2", // Version claire de l'orange pour les backgrounds
  lightGreen: "#CCFFCC", // Version claire du vert pour certains éléments
  gray: "#718096",
};

const PAGE_COMPONENTS = {
  Imbox: Imbox,
  Products,
  ProductDet: AProductDet,
  ProductUpdat: AProductUpdat,
  AddProductA,
  ACustomers,
  AOrders,
  ACustomerDet,
  AodersDet,
  AddFournisseur,
  AddCategorie,
  ProductPub,
  Livraison,
  AdminSettings,
  PromoCodes,
  AddPromoCode: AddEditPromoCode,
  EditPromoCode: AddEditPromoCode,
  PromoCodeDetail,
};

function AdminDashboard({ allCategories, allProducts }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const admin = JSON.parse(localStorage.getItem("AdminEcomme"));
  const [unreadCounts, setUnreadCounts] = useState(0);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleLogout = () => {
    localStorage.removeItem("AdminEcomme");
    navigate("/login");
  };

  const PageComponent = PAGE_COMPONENTS[params.op] || Overview;

  // Fonction pour déterminer si un item est actif
  const isActive = (to) => {
    if (to === "/Admin" && !params.op) return true;
    if (params.op && to.includes(params.op)) return true;
    return false;
  };

  const SIDEBAR_ITEMS = [
    {
      icon: Home,
      label: "Tableau de Bord",
      to: "/Admin",
    },

    {
      icon: Inbox,
      label: "Boîte de Réception",
      to: "/Admin/Imbox",
      badge: unreadCounts || 0,
    },
    {
      icon: Package,
      label: "Produits",
      to: "/Admin/Products",
    },
    {
      icon: Users,
      label: "Clients",
      to: "/Admin/ACustomers",
    },
    {
      icon: ShoppingCart,
      label: "Commandes",
      to: "/Admin/AOrders",
    },
    {
      icon: PlusCircle,
      label: "Ajouter Produit",
      to: "/Admin/AddProductA",
    },
    {
      icon: Tags,
      label: "Ajouter Catégorie",
      to: "/Admin/AddCategorie",
    },
    {
      icon: Newspaper,
      label: "Publications Produits",
      to: "/Admin/ProductPub",
    },
    {
      icon: Tags,
      label: "Codes Promo",
      to: "/Admin/PromoCodes",
    },
    {
      icon: Newspaper,
      label: "Tarifs Livraison",
      to: "/Admin/Livraison",
    },
    {
      icon: Settings,
      label: "Admin Settings",
      to: "/Admin/AdminSettings",
    },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Sidebar */}
      <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            className="fixed top-4 left-4 z-50 md:hidden text-white bg-green-600 hover:bg-green-700"
            style={{ backgroundColor: COLORS.green }}
          >
            <Menu />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-72 p-0 border-r-4"
          style={{ borderColor: COLORS.orange }}
        >
          <div
            className="p-6 flex items-center justify-between"
            style={{ backgroundColor: COLORS.green }}
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden">
                {/* Emplacement pour le logo */}
                <span
                  className="text-lg font-bold"
                  style={{ color: COLORS.orange }}
                >
                  K
                </span>
              </div>
              <h2 className="text-xl font-bold text-white">KASSARMOU</h2>
            </div>
          </div>
          <nav className="p-4 space-y-1">
            {SIDEBAR_ITEMS.map((item, index) => (
              <Link
                key={index}
                to={item.to}
                onClick={() => setIsSidebarOpen(false)}
                className={`flex items-center justify-between py-3 px-3 rounded-lg transition-colors group ${
                  isActive(item.to)
                    ? "bg-orange-100 text-orange-600"
                    : "hover:bg-orange-50"
                }`}
                style={{
                  backgroundColor: isActive(item.to)
                    ? COLORS.lightOrange
                    : "transparent",
                  color: isActive(item.to) ? COLORS.orange : COLORS.gray,
                }}
              >
                <div className="flex items-center space-x-3">
                  <item.icon
                    className={`w-5 h-5 transition-colors`}
                    style={{
                      color: isActive(item.to) ? COLORS.orange : COLORS.gray,
                    }}
                  />
                  <span className="text-sm font-medium">{item.label}</span>
                </div>
                {item.badge && (
                  <Badge
                    className="rounded-full px-2 py-0.5 text-xs text-white"
                    style={{ backgroundColor: COLORS.orange }}
                  >
                    {item.badge}
                  </Badge>
                )}
              </Link>
            ))}

            <Button
              className="w-full mt-6 flex items-center justify-center space-x-2 text-white"
              style={{ backgroundColor: COLORS.orange }}
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" />
              <span>Déconnexion</span>
            </Button>
          </nav>
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar */}
      <div
        className="hidden md:block w-72 bg-white shadow-lg"
        style={{ borderRight: `4px solid ${COLORS.orange}` }}
      >
        <div
          className="p-6 flex items-center justify-between"
          style={{ backgroundColor: COLORS.green }}
        >
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center overflow-hidden">
              {/* Emplacement pour le logo */}
              <span
                className="text-xl font-bold"
                style={{ color: COLORS.orange }}
              >
                K
              </span>
            </div>
            <h2 className="text-2xl font-bold text-white">KASSARMOU</h2>
          </div>
        </div>
        <nav className="p-4 space-y-1">
          {SIDEBAR_ITEMS.map((item, index) => (
            <Link
              key={index}
              to={item.to}
              className={`flex items-center justify-between py-3 px-3 rounded-lg transition-all duration-200 group ${
                isActive(item.to) ? "text-orange-600" : "hover:bg-orange-50"
              }`}
              style={{
                backgroundColor: isActive(item.to)
                  ? COLORS.lightOrange
                  : "transparent",
                color: isActive(item.to) ? COLORS.orange : COLORS.gray,
                transform: isActive(item.to)
                  ? "translateX(5px)"
                  : "translateX(0)",
              }}
            >
              <div className="flex items-center space-x-3">
                <item.icon
                  className={`w-5 h-5 transition-colors`}
                  style={{
                    color: isActive(item.to) ? COLORS.orange : COLORS.gray,
                  }}
                />
                <span className="text-sm font-medium">{item.label}</span>
              </div>
              {item.badge && (
                <Badge
                  className="rounded-full px-2 py-0.5 text-xs text-white"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  {item.badge}
                </Badge>
              )}
            </Link>
          ))}

          <Button
            className="w-full mt-6 flex items-center justify-center space-x-2 text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: COLORS.orange }}
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" />
            <span>Déconnexion</span>
          </Button>
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Navigation */}
        <div
          className="bg-white shadow-md p-4 flex justify-between items-center"
          style={{ borderBottom: `3px solid ${COLORS.green}` }}
        >
          <div className="flex items-center">
            <Button
              variant="ghost"
              className="mr-4 md:hidden"
              style={{ color: COLORS.green }}
              onClick={toggleSidebar}
            >
              <Menu />
            </Button>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="search"
                placeholder="Rechercher..."
                className="pl-8 border-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              className="hidden sm:flex hover:bg-gray-100"
            >
              <MessageCircle style={{ color: COLORS.green }} />
              <span className="sr-only">Messages</span>
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="relative hidden sm:flex hover:bg-gray-100"
            >
              <Bell style={{ color: COLORS.green }} />
              <Badge
                className="absolute -top-2 -right-2 text-white"
                style={{ backgroundColor: COLORS.orange }}
              >
                3
              </Badge>
              <span className="sr-only">Notifications</span>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center space-x-2 cursor-pointer p-1 rounded-full hover:bg-gray-100">
                  <Avatar
                    className="border-2"
                    style={{ borderColor: COLORS.orange }}
                  >
                    <AvatarImage src="/placeholder-avatar.png" />
                    <AvatarFallback
                      style={{
                        backgroundColor: COLORS.lightGreen,
                        color: COLORS.green,
                      }}
                    >
                      {admin?.name?.[0] || "A"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline font-medium">
                    {admin?.name || "Admin"}
                  </span>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="cursor-pointer hover:bg-gray-100">
                  Profil
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer hover:bg-gray-100">
                  Paramètres
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-600 cursor-pointer hover:bg-red-50"
                  onClick={handleLogout}
                >
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main Content with colored header */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="p-4 bg-gradient-to-r from-green-600 to-green-500 text-white shadow-md">
            <h1 className="text-2xl font-bold">
              {params.op
                ? params.op.replace(/([A-Z])/g, " $1").trim()
                : "Tableau de Bord"}
            </h1>
            <p className="text-sm text-green-100">
              Bienvenue dans votre espace d'administration Kassarmou
            </p>
          </div>

          <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <PageComponent
                allCategories={allCategories}
                allProducts={allProducts}
                setUnreadCounts={setUnreadCounts}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
